import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import CrmModal from '../components/CrmModal';
import { money } from '../../lib/format';
import { IconServices } from '../components/SvgIcons';

const WORK_STATUSES = ['Waiting to Start', 'In Progress', 'Waiting for Client', 'On Hold', 'Completed', 'Cancelled'];

export default function ServiceOrderModal({ clientName, order, availableServices, currency, onClose, onSaved, canViewFinance = true }) {
  const isEdit = Boolean(order);
  const [unitPrice, setUnitPrice] = useState(0);
  const [form, setForm] = useState({
    serviceName: '',
    serviceId: '',
    quantity: 1,
    unit: 'Units',
    price: '',
    discountType: 'Fixed',
    discountValue: 0,
    amountReceived: 0,
    startDate: '',
    endDate: '',
    renewalDate: '',
    nextPaymentDate: '',
    workStatus: 'In Progress',
    assignedStaff: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [staffList, setStaffList] = useState([]);

  useEffect(() => {
    api.get('/api/staff-module/staff')
      .then((data) => setStaffList(data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (order) {
      const q = order.quantity || 1;
      const p = order.price ?? 0;
      setUnitPrice(q > 0 ? Math.round(p / q) : p);
      setForm({
        serviceName: order.serviceName || '',
        serviceId: order.serviceId || '',
        quantity: q,
        unit: order.unit || 'Units',
        price: p,
        discountType: order.discountType || 'Fixed',
        discountValue: order.discountValue || 0,
        amountReceived: order.amountReceived || 0,
        startDate: order.startDate || '',
        endDate: order.endDate || '',
        renewalDate: order.renewalDate || '',
        nextPaymentDate: order.nextPaymentDate || '',
        workStatus: order.workStatus || 'In Progress',
        assignedStaff: order.assignedStaff || '',
        notes: order.notes || '',
      });
    }
  }, [order]);

  function set(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleStartDateChange(val) {
    setForm((prev) => {
      const next = { ...prev, startDate: val };
      if (val && !prev.renewalDate) {
        try {
          const parts = val.split('-');
          if (parts.length === 3) {
            const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
            const qtyMonths = Number(prev.quantity) || 1;
            d.setMonth(d.getMonth() + qtyMonths);
            d.setDate(d.getDate() - 3); // 3 days before month end
            const pad = (n) => String(n).padStart(2, '0');
            next.renewalDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

            if (!prev.endDate) {
              const endD = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
              endD.setMonth(endD.getMonth() + qtyMonths);
              next.endDate = `${endD.getFullYear()}-${pad(endD.getMonth() + 1)}-${pad(endD.getDate())}`;
            }
          }
        } catch {
          // ignore date parse errors
        }
      }
      return next;
    });
  }

  function handleServiceSelect(e) {
    const svc = availableServices.find((s) => s.id === e.target.value);
    if (!svc) { set('serviceName', ''); set('serviceId', ''); return; }
    const basePrice = svc.defaultPrice || 0;
    setUnitPrice(basePrice);
    const qty = Number(form.quantity) || 1;
    setForm((prev) => ({
      ...prev,
      serviceName: svc.serviceName,
      serviceId: svc.id,
      unit: svc.unit || 'Units',
      price: basePrice * qty,
    }));
  }

  function handleQuantityChange(val) {
    const qty = Number(val) || 1;
    setForm((prev) => {
      const next = { ...prev, quantity: val };
      if (unitPrice > 0) {
        next.price = unitPrice * qty;
      }
      if (prev.startDate) {
        try {
          const parts = prev.startDate.split('-');
          if (parts.length === 3) {
            const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
            d.setMonth(d.getMonth() + qty);
            d.setDate(d.getDate() - 3);
            const pad = (n) => String(n).padStart(2, '0');
            next.renewalDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

            const endD = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
            endD.setMonth(endD.getMonth() + qty);
            next.endDate = `${endD.getFullYear()}-${pad(endD.getMonth() + 1)}-${pad(endD.getDate())}`;
          }
        } catch {}
      }
      return next;
    });
  }

  function handlePriceInputChange(val) {
    const newPrice = Number(val) || 0;
    const qty = Number(form.quantity) || 1;
    if (qty > 0) {
      setUnitPrice(Math.round(newPrice / qty));
    }
    set('price', val);
  }

  // Computed financials preview
  const priceNum = Number(form.price) || 0;
  const discountVal = Number(form.discountValue) || 0;
  const discountAmt = form.discountType === 'Percentage'
    ? Math.round((priceNum * discountVal) / 100)
    : discountVal;
  const finalAmount = Math.max(0, priceNum - discountAmt);
  const received = Number(form.amountReceived) || 0;
  const pending = Math.max(0, finalAmount - received);
  const payStatus = received >= finalAmount && finalAmount > 0 ? 'Paid' : received > 0 ? 'Partial' : 'Pending';

  const payStatusColor = { Paid: '#224eae', Partial: '#0f172a', Pending: '#0f172a' };

  async function handleSubmit(e) {
    e?.preventDefault();
    if (!form.serviceName.trim()) { setErr('Service Name is required'); return; }
    if (!form.startDate) { setErr('Start Date is required'); return; }
    if (!form.renewalDate) { setErr('Renewal Date is required so it shows in Renewals Dashboard'); return; }

    setSaving(true);
    setErr('');
    try {
      await onSaved({ ...form, price: priceNum, discountAmt });
    } catch (ex) {
      setErr(ex.message || 'Failed to save order');
    } finally {
      setSaving(false);
    }
  }

  return (
    <CrmModal
      onClose={onClose}
      title={isEdit ? `Edit Service Order — ${order.serviceName}` : `Add New Service Order`}
      subtitle={`Client: ${clientName}`}
      icon={IconServices}
      maxWidth={720}
      footer={
        <>
          <button type="button" className="cm-btn-outline" onClick={onClose}>Cancel</button>
          <button type="button" className="cm-btn-emerald" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Update Order' : 'Add Service Order'}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        {err && <div className="err" style={{ marginBottom: 14, fontWeight: 700 }}>{err}</div>}

        {/* Service Selection */}
        <div className="cm-form-section-card" style={{ marginBottom: 16 }}>
          <div className="cm-form-section-title">Service Details</div>
          <div className="cm-grid-2">
            <div className="cm-field-group">
              <label>Select from Catalog</label>
              <select value={form.serviceId} onChange={handleServiceSelect}>
                <option value="">— Pick a service —</option>
                {availableServices.filter((s) => s.status !== 'Inactive').map((s) => (
                  <option key={s.id} value={s.id}>{s.serviceName} ({s.unit}){canViewFinance ? ` — ₹${s.defaultPrice?.toLocaleString()}` : ''}</option>
                ))}
              </select>
            </div>
            <div className="cm-field-group">
              <label>Service Name *</label>
              <input
                value={form.serviceName}
                onChange={(e) => set('serviceName', e.target.value)}
                placeholder="e.g. Meta Ads - August 2026"
                required
              />
            </div>
          </div>

          <div className="cm-grid-2">
            <div className="cm-field-group">
              <label>Quantity / Months</label>
              <input type="number" min="1" value={form.quantity} onChange={(e) => handleQuantityChange(e.target.value)} />
            </div>
            <div className="cm-field-group">
              <label>Unit</label>
              <input value={form.unit} onChange={(e) => set('unit', e.target.value)} placeholder="Months, Reels, Graphics…" />
            </div>
          </div>
        </div>

        {/* Pricing */}
        {canViewFinance && (
          <div className="cm-form-section-card" style={{ marginBottom: 16 }}>
            <div className="cm-form-section-title">Pricing & Discount</div>
            <div className="cm-grid-2">
              <div className="cm-field-group">
                <label>Price (₹) {unitPrice > 0 && <span style={{ color: 'var(--crm-emerald)', fontSize: '0.78rem' }}>({unitPrice.toLocaleString()} × {form.quantity})</span>} *</label>
                <input type="number" min="0" value={form.price} onChange={(e) => handlePriceInputChange(e.target.value)} placeholder="0" />
              </div>
              <div className="cm-field-group">
                <label>Discount Type</label>
                <select value={form.discountType} onChange={(e) => set('discountType', e.target.value)}>
                  <option value="Fixed">Fixed Amount (₹)</option>
                  <option value="Percentage">Percentage (%)</option>
                </select>
              </div>
              <div className="cm-field-group">
                <label>Discount Value {form.discountType === 'Percentage' ? '(%)' : '(₹)'}</label>
                <input type="number" min="0" value={form.discountValue} onChange={(e) => set('discountValue', e.target.value)} />
              </div>
              <div className="cm-field-group">
                <label>Amount Received (₹)</label>
                <input type="number" min="0" value={form.amountReceived} onChange={(e) => set('amountReceived', e.target.value)} />
              </div>
            </div>

            {/* Financials Preview */}
            <div style={{ background: 'var(--crm-emerald-light)', border: '1px solid var(--crm-border)', borderRadius: 10, padding: '12px 16px', marginTop: 8 }}>
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: '0.85rem', fontWeight: 700 }}>
                <span>Price: <strong>{money(priceNum, currency)}</strong></span>
                {discountAmt > 0 && <span style={{ color: 'var(--crm-text-muted)' }}>Discount: <strong>- {money(discountAmt, currency)}</strong></span>}
                <span style={{ color: 'var(--crm-emerald)' }}>Final: <strong>{money(finalAmount, currency)}</strong></span>
                <span style={{ color: 'var(--crm-emerald)' }}>Received: <strong>{money(received, currency)}</strong></span>
                <span style={{ color: 'var(--crm-text-main)' }}>Pending: <strong>{money(pending, currency)}</strong></span>
                <span style={{ color: payStatusColor[payStatus] }}>Status: <strong>{payStatus}</strong></span>
              </div>
            </div>
          </div>
        )}

        {/* Dates */}
        <div className="cm-form-section-card" style={{ marginBottom: 16 }}>
          <div className="cm-form-section-title">Dates (Required for Renewals)</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 }}>
            <div className="cm-field-group">
              <label>Start Date *</label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => handleStartDateChange(e.target.value)}
                required
              />
            </div>
            <div className="cm-field-group">
              <label>End Date</label>
              <input type="date" value={form.endDate} onChange={(e) => set('endDate', e.target.value)} />
            </div>
            <div className="cm-field-group">
              <label>Renewal Date *</label>
              <input
                type="date"
                value={form.renewalDate}
                onChange={(e) => set('renewalDate', e.target.value)}
                required
              />
            </div>
            <div className="cm-field-group">
              <label>Next Payment Date</label>
              <input type="date" value={form.nextPaymentDate} onChange={(e) => set('nextPaymentDate', e.target.value)} />
            </div>
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--crm-emerald)', marginTop: 8, fontWeight: 600 }}>
            💡 Start Date પસંદ કરશો એટલે Renewal Date ઓટોમેટિકલી સજેસ્ટ થઈ જશે.
          </div>
        </div>

        {/* Work & Staff */}
        <div className="cm-form-section-card" style={{ marginBottom: 0 }}>
          <div className="cm-form-section-title">Work Status & Assignment</div>
          <div className="cm-grid-2">
            <div className="cm-field-group">
              <label>Work Status</label>
              <select value={form.workStatus} onChange={(e) => set('workStatus', e.target.value)}>
                {WORK_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="cm-field-group">
              <label>Assigned Staff</label>
              <select value={form.assignedStaff} onChange={(e) => set('assignedStaff', e.target.value)}>
                <option value="">— Select Assigned Staff —</option>
                {staffList.filter((s) => s.status !== 'Inactive').map((st) => (
                  <option key={st.id} value={st.name}>
                    {st.name} {st.roleName ? `(${st.roleName})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="cm-field-group">
            <label>Notes</label>
            <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} placeholder="Any specific notes for this order…" />
          </div>
        </div>
      </form>
    </CrmModal>
  );
}
